/**
 * The CRM page the embedded frame lives in, and how the frame talks to it
 * (#746, spec `docs/plans/2026-10-29-uspacy-card-tab-session.md` §5, §10).
 *
 * Two kinds of host. Bitrix24 opens the frame as a named portal iframe and
 * talks through its SDK (`@bitrix24/b24jssdk`); the session rides an HttpOnly
 * cookie. A host whose session is a bearer (Uspacy — the catalog fact
 * `embeddedSessionCredential: 'bearer'`) is a micro-frontend that mounts a
 * plain iframe: no SDK, no cookie. There the frame keeps the session id in
 * this module's memory, sends it as `Authorization: Bearer`, and speaks to the
 * page by `postMessage` in the `aelo:*` protocol of §10.1 — only to the origin
 * the server named in the bootstrap exchange (`crmOrigin`), and only listening
 * to the parent window from that origin.
 *
 * This is the ONE place that tells the two apart: screens ask
 * `embeddedHostUsesMessages(vendor)` rather than comparing a vendor name.
 *
 * Memory, not `sessionStorage`/`localStorage`: storage would outlive the frame
 * and be readable by every script of the origin later, and a reload has no
 * bootstrap to exchange anyway — the host issues a new frame address instead.
 */
import {
  crmVendorsWithEmbeddedApp,
  embeddedSessionUsesBearer,
  getIntegrationDisplayName,
  type EmbeddedCrmVendorId,
} from '@aica/core';
import {
  EMBEDDED_FRAME_MAX_HEIGHT_PX,
  parseCrmTabEntityId,
  type EmbeddedFrameToHostMessage,
  type EmbeddedHostEntityType,
  type EmbeddedHostToFrameMessage,
} from '@aica/api-types';

// ============================================================================
// Session credential
// ============================================================================

/** `host=` of the frame address, captured by `embedded-session` at load. */
let hostHint: EmbeddedCrmVendorId | null = null;

/**
 * What the bootstrap exchange answered; `null` until it has. A token means the
 * session is a bearer one; without a token it is the cookie session.
 */
let exchanged: { sessionToken: string | null; crmOrigin: string | null } | null = null;

/**
 * The prefix of the iframe's `name` that the page of a message host sets
 * (`aelo-host:uspacy`). The address loses `host=` when `embedded-session`
 * scrubs it, so a reloaded frame has no `host=` left; `window.name` survives a
 * reload and navigations inside the frame, and it is the second place the
 * hint is read from. A Bitrix24 frame has a name of its own, without this
 * prefix.
 */
export const EMBEDDED_HOST_NAME_PREFIX = 'aelo-host:';

/** The host a frame name carries (`aelo-host:<vendor>`), or `null`. */
export function hostHintFromFrameName(
  windowName: string | null | undefined
): string | null {
  if (!windowName?.startsWith(EMBEDDED_HOST_NAME_PREFIX)) return null;
  return windowName.slice(EMBEDDED_HOST_NAME_PREFIX.length);
}

/**
 * Remember the vendor the frame address names (`?host=`), or else the frame
 * name does (`aelo-host:<vendor>`). Kept only when it is a CRM with an
 * embedded app; anything else is ignored. Called once, by `embedded-session`
 * while it scrubs the address.
 */
export function rememberEmbeddedHostHint(
  value: string | null,
  windowName?: string | null
): void {
  const known: readonly string[] = crmVendorsWithEmbeddedApp();
  const named = value ?? hostHintFromFrameName(windowName);
  hostHint = named && known.includes(named) ? (named as EmbeddedCrmVendorId) : null;
}

/** The vendor the frame address named — what is known before the server answers. */
export function embeddedHostHint(): EmbeddedCrmVendorId | null {
  return hostHint;
}

/** `scheme://host[:port]` of an http(s) address, or `null`. */
function originOf(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * Record the answer of a successful bootstrap exchange. A `sessionToken` makes
 * this a bearer session (it goes into `Authorization` from now on) and its
 * `crmOrigin` the only window messages may go to; no token — the cookie
 * session, and nothing is kept.
 */
export function setEmbeddedSessionCredential(answer: {
  sessionToken?: string;
  crmOrigin?: string;
}): void {
  const sessionToken = answer.sessionToken ?? null;
  exchanged = {
    sessionToken,
    crmOrigin: sessionToken ? originOf(answer.crmOrigin) : null,
  };
}

/**
 * The credential header of an embedded session request: `Authorization:
 * Bearer …` for a bearer session, nothing for the cookie one. Every request of
 * the embedded session spreads this into its headers.
 */
export function embeddedSessionAuthHeaders(): Record<string, string> {
  const token = exchanged?.sessionToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** The CRM page origin the server named in the exchange; `null` before it or for a cookie session. */
export function embeddedCrmOrigin(): string | null {
  return exchanged?.crmOrigin ?? null;
}

// ============================================================================
// Which host
// ============================================================================

/**
 * Does the frame of this vendor talk to its page by `aelo:*` messages, with no
 * Bitrix24 SDK? The catalog fact behind it is the bearer credential: a host
 * that gives its frame no cookie is the micro-frontend host that mounts a
 * plain iframe. No vendor — the Bitrix24 frame, as every embedded tab was.
 */
export function embeddedHostUsesMessages(vendor: string | null | undefined): boolean {
  return embeddedSessionUsesBearer(vendor);
}

/**
 * The same question for code outside React (the fetch layer's recovery): the
 * hint of a message host (`host=` of the address, else the frame name) is a
 * yes ALWAYS — a reloaded frame has no exchange to read it from, and an
 * exchange that came back without a token must not turn a Uspacy frame into a
 * cookie one (the origin may hold a Bitrix24 session cookie of the same
 * browser). Without a hint a token in the answer is the bearer host.
 */
export function frameUsesHostMessages(): boolean {
  if (embeddedHostUsesMessages(hostHint)) return true;
  return exchanged?.sessionToken != null;
}

/**
 * The embedded dashboard's «Settings» tab configures delivery into Bitrix24
 * fields and smart processes; a message host has nothing of that kind
 * (spec 2026-10-24-uspacy-embedded-tab.md §3.6).
 */
export function embeddedHostOffersDeliverySettings(
  vendor: string | null | undefined
): boolean {
  return !embeddedHostUsesMessages(vendor);
}

/**
 * The same question for code outside React — a route map, a link target — that
 * must agree with the dashboard hiding the «Settings» tab: the frame's own
 * answer (`frameUsesHostMessages`), not a vendor read from a context.
 */
export function frameOffersDeliverySettings(): boolean {
  return !frameUsesHostMessages();
}

/**
 * The height class of a full-screen gate (consent, access, compliance,
 * onboarding). In Bitrix24 the frame is the page's height and a gate fills it
 * as before; in a message host the frame follows its content (`aelo:height`),
 * so a gate sized by the viewport would hold the frame at its tallest height
 * after a taller screen and never let it shrink again.
 */
export function embeddedGateHeightClass(
  vendor: string | null | undefined,
  bitrix24: 'h-screen' | 'min-h-screen'
): string {
  return embeddedHostUsesMessages(vendor) ? 'min-h-0' : bitrix24;
}

/**
 * The portal domain the "Task in Bitrix24" link is built from: a Bitrix24
 * portal's own, and `null` for a message host, which has no such domain.
 */
export function embeddedBitrix24Domain(
  vendor: string | null | undefined,
  domain: string | null
): string | null {
  return embeddedHostUsesMessages(vendor) ? null : domain;
}

/** The vendor of an embedded frame when nothing says otherwise. */
const DEFAULT_EMBEDDED_VENDOR: EmbeddedCrmVendorId = 'bitrix24';

/** The CRM's name for texts like «reopen via …». */
export function embeddedHostDisplayName(vendor: string | null | undefined): string {
  return getIntegrationDisplayName(vendor ?? DEFAULT_EMBEDDED_VENDOR);
}

/** «Session expired, reopen from <the CRM>» — one key per embedded CRM. */
const REOPEN_KEY: Readonly<Record<EmbeddedCrmVendorId, string>> = {
  bitrix24: 'dashboard:embedded.errors.sessionExpiredReopenFromBitrix24',
  uspacy: 'dashboard:embedded.errors.sessionExpiredReopenFromUspacy',
};

export function embeddedSessionExpiredKey(vendor: string | null | undefined): string {
  const known: readonly string[] = crmVendorsWithEmbeddedApp();
  const id = vendor && known.includes(vendor) ? (vendor as EmbeddedCrmVendorId) : null;
  return REOPEN_KEY[id ?? DEFAULT_EMBEDDED_VENDOR];
}

// ============================================================================
// The `aelo:*` protocol (§10.1)
// ============================================================================

/**
 * The card entities the host opens and navigates to. The shapes of the messages
 * and the height bound are the protocol's (`@aica/api-types`, `uspacy.ts`); the
 * id is read by the one reader every entry of the card tab uses.
 */
const HOST_ENTITY_TYPES: Readonly<Record<EmbeddedHostEntityType, true>> = {
  deal: true,
  lead: true,
};

export interface HostEntity {
  entityType: EmbeddedHostEntityType;
  entityId: string;
}

/** The entity a message names, or `null`: a known type and a string id of the SSOT's format. */
function readHostEntity(entityType: unknown, entityId: unknown): HostEntity | null {
  if (
    typeof entityType !== 'string' ||
    !Object.prototype.hasOwnProperty.call(HOST_ENTITY_TYPES, entityType)
  ) {
    return null;
  }
  // A string only: the protocol carries the id as one, and a number is not it.
  const id = typeof entityId === 'string' ? parseCrmTabEntityId(entityId) : null;
  return id ? { entityType: entityType as EmbeddedHostEntityType, entityId: id } : null;
}

/**
 * Post a message to the CRM page — to `crmOrigin` and nowhere else. Without
 * it (no bearer exchange happened) nothing is sent: never `*`, never
 * `document.referrer`. Returns whether the message went.
 */
function postToHost(message: EmbeddedFrameToHostMessage): boolean {
  const origin = embeddedCrmOrigin();
  if (!origin || typeof window === 'undefined') return false;
  window.parent.postMessage(message, origin);
  return true;
}

/** Tell the page the session lapsed; the host issues a new one (§10.2). */
export function postHostSessionExpired(): boolean {
  return postToHost({ type: 'aelo:session-expired' });
}

/**
 * Tell the page the frame now hears `aelo:navigate`: the listener is mounted.
 * A navigate sent earlier — while the frame loaded or stood on a consent or
 * compliance gate — was lost; the page sends the card it wants after this.
 */
export function postHostReady(): boolean {
  return postToHost({ type: 'aelo:ready' });
}

/** Ask the page to open a deal or lead card of the CRM. */
export function postHostOpenEntity(entityType: string, entityId: string): boolean {
  const entity = readHostEntity(entityType, entityId);
  if (!entity) return false;
  return postToHost({ type: 'aelo:open-entity', ...entity });
}

/** Tell the page how tall the frame's content is: an integer in 1…20000. */
export function postHostHeight(height: number): boolean {
  if (!Number.isFinite(height)) return false;
  const rounded = Math.min(EMBEDDED_FRAME_MAX_HEIGHT_PX, Math.ceil(height));
  if (rounded < 1) return false;
  return postToHost({ type: 'aelo:height', height: rounded });
}

/**
 * The card an `aelo:navigate` asks for, or `null` for anything else: a message
 * not from the parent window, not from `crmOrigin`, of another type or with a
 * field outside the protocol is dropped silently.
 */
export function readHostNavigateMessage(event: MessageEvent): HostEntity | null {
  const origin = embeddedCrmOrigin();
  if (!origin || typeof window === 'undefined') return null;
  if (event.source !== window.parent || event.origin !== origin) return null;
  const data: unknown = event.data;
  if (typeof data !== 'object' || data === null) return null;
  const message: Partial<Record<keyof EmbeddedHostToFrameMessage, unknown>> = data;
  if (message.type !== 'aelo:navigate') return null;
  return readHostEntity(message.entityType, message.entityId);
}

/** Listen for `aelo:navigate` from the page; returns the unsubscribe. */
export function listenForHostNavigation(
  onNavigate: (entity: HostEntity) => void
): () => void {
  const onMessage = (event: MessageEvent) => {
    const entity = readHostNavigateMessage(event);
    if (entity) onNavigate(entity);
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

/**
 * Keep the page told how tall the document is (`aelo:height`): now, and on
 * every change of the body's box — at most once a frame, and only when the
 * number changed. The body's box rather than `scrollHeight`, which never drops
 * below the frame and so could only ever grow it (`fitContentHeightFrame`
 * measures the same way for Bitrix24). Returns the stop function.
 */
export function observeHostHeight(): () => void {
  if (typeof document === 'undefined') return () => {};
  let last = 0;
  let pending: number | null = null;
  const send = () => {
    const height = Math.ceil(document.body.getBoundingClientRect().height);
    if (height <= 0 || height === last) return;
    if (postHostHeight(height)) last = height;
  };
  send();
  if (typeof ResizeObserver === 'undefined') return () => {};
  const observer = new ResizeObserver(() => {
    if (pending !== null) return;
    pending = requestAnimationFrame(() => {
      pending = null;
      send();
    });
  });
  observer.observe(document.body);
  return () => {
    observer.disconnect();
    if (pending !== null) cancelAnimationFrame(pending);
    pending = null;
  };
}

/** Test seam: forget the hint and the exchange. */
export function resetEmbeddedHostForTests(): void {
  hostHint = null;
  exchanged = null;
}
